import { Burden, Callback } from '@deqo/foundation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce, useWindowSize } from 'react-use';
import { pxInRem, uuid } from '../CodesThatsGottaGoSomewhereElse';

/**
 * Custom React hook that mechanism for loading late immutable values for a component.
 *
 * @param loader The async function that does the loading.
 */
export function useLoaderOld<T>(loader: Callback<T | Promise<T>>): [boolean, T, Error?] {
    const [result, setResult] = useState<T>();
    const [error, setError] = useState<Error>();
    useEffectOnce(() => {
        try {
            const r = loader();
            if (r instanceof Promise) r.then(setResult).catch(setError);
            else setResult(r);
        }
        catch (err) {
            setError(err);
        }
    });
    return [
        result !== undefined || error !== undefined,
        result,
        error
    ];
}

export function useLoader2<T>(loader: Callback<T | Promise<T>>, deps: React.DependencyList): [boolean, T, Error | null, UseLoaderCallbacks<T>] {
    const tracker = useRef(uuid());
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState<T>();
    const [error, setError] = useState<Error>();
    const refresh = useCallback(() => {
        return new Promise((rs) => {
            try {
                const t = uuid();
                tracker.current = t;
                setLoading(true);
                const r = loader();
                if (r instanceof Promise) {
                    r
                        .then(r => {
                            if (t !== tracker.current) return;
                            setValue(r);
                        })
                        .catch(e => {
                            if (t !== tracker.current) return;
                            setValue(null);
                            setError(e);
                        })
                        .finally(() => {
                            if (t !== tracker.current) return;
                            setLoading(false);
                            rs(null);
                        });
                }
                else {
                    setValue(r);
                    setLoading(false);
                    rs(null);
                }
            }
            catch (err) {
                setError(err);
                setLoading(false);
                rs(null);
            }
        });
    }, deps);
    useEffect(() => void (refresh()), deps);
    return [
        loading,
        value,
        error,
        { refresh, update: setValue },
    ];
}

/**
 * Custom React hook that mechanism for loading late immutable values for a component.
 *
 * @param loader The async function that does the loading.
 */
// eslint-disable-next-line max-len
export function useLoaderWithRefresh<T>(loader: Callback<T | Promise<T>>, deps: React.DependencyList): [boolean, T, Error | null, () => Promise<any>] {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<T>();
    const [error, setError] = useState<Error>();
    const refresh = useCallback(() => {
        return new Promise((rs) => {
            try {
                setLoading(true);
                const r = loader();
                if (r instanceof Promise) {
                    r.then(setResult).catch(setError).finally(() => {
                        setLoading(false);
                        rs(null);
                    });
                }
                else {
                    setResult(r);
                    setLoading(false);
                    rs(null);
                }
            }
            catch (err) {
                setError(err);
                setLoading(false);
                rs(null);
            }
        });
    }, deps);
    useEffectOnce(() => void (refresh()));
    return [
        loading,
        result,
        error,
        refresh,
    ];
}

export type UseLoaderCallbacks<T> = {
    refresh: () => Promise<any>,
    update: React.Dispatch<React.SetStateAction<T>>,
};

/**
 * Custom React hook that mechanism for loading late immutable values for a component.
 *
 * @param loader The async function that does the loading.
 */
// eslint-disable-next-line max-len
export function useLoader<T>(loader: Callback<T | Promise<T>>, deps: React.DependencyList): [boolean, T, Error | null, UseLoaderCallbacks<T>] {
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState<T>();
    const [error, setError] = useState<Error>();
    const refresh = useCallback(() => {
        return new Promise((rs) => {
            try {
                setLoading(true);
                const r = loader();
                if (r instanceof Promise) {
                    r.then(setResult).catch(setError).finally(() => {
                        setLoading(false);
                        rs(null);
                    });
                }
                else {
                    setResult(r);
                    setLoading(false);
                    rs(null);
                }
            }
            catch (err) {
                setError(err);
                setLoading(false);
                rs(null);
            }
        });
    }, deps);
    useEffectOnce(() => void (refresh()));
    return [
        loading,
        result,
        error,
        { refresh, update: setResult },
    ];
}

const SubmitterFailure = (error: Error) => ({ id: uuid(), ok: false, error, time: Date.now() }) as SubmitterResult;
const SubmitterSuccess = (data?: any) => ({ id: uuid(), ok: true, data, time: Date.now() }) as SubmitterResult;

export interface SubmitterResult {
    id: string;
    ok: boolean;
    data?: any;
    error: Error;
    time: number;
}

/**
 * Custom React hook that mechanism for submitting state to an api and handling the errors and working state, etc.
 *
 * @param loader The async function that does the loading.
 */
// eslint-disable-next-line max-len
export function useSubmitter<T extends (...args: any[]) => Promise<any>>(callback: T, deps: React.DependencyList): [T, boolean, SubmitterResult | null] {
    const [working, setWorking] = useState(false);
    const [result, setResult] = useState<SubmitterResult>(null);
    const wrappedCallback: any = useCallback((...args: any[]) => {
        return new Promise((rs) => {
            setWorking(true);
            callback(...args)
                .then(data => {
                    setResult(SubmitterSuccess(data));
                    rs(data);
                })
                .catch(err => void (setResult(SubmitterFailure(err))))
                .finally(() => void (setWorking(false)));
        });
    }, deps);
    return [
        wrappedCallback,
        working,
        result,
    ];
}

/**
 * Custom React hook that provides access to window events.
 *
 * @param event The event
 * @param listener The listener
 * @param options The listener options
 */
export const useWindowEvent = <K extends keyof WindowEventMap>(
    event: K,
    listener: (e: WindowEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions) => {
    useEffect(() => {
        window.addEventListener(event, listener, options);
        return () => window.removeEventListener(event, listener);
    }, [event, listener, options]);
};

export function useBurden<T extends Burden>(provider: () => T): T {
    const ref = useRef(null as T);
    useEffect(() => () => {
        if (ref.current) {
            Burden.resolve(ref.current);
            ref.current = null;
        }
    }, []);
    if (!ref.current) {
        ref.current = provider();
    }
    return ref.current;
}

type RecordMutator<T> = {
    (key: keyof T, value: any): void,
    (value: T): void,
};

export function useStateRecord<T>(initial?: Partial<T>): [T, RecordMutator<T>] {
    const [model, setModel] = useState(initial);
    return [model as any, (...args: any[]) => {
        if (args.length > 1) setModel(x => Object.assign({}, x, { [args[0]]: args[1] }));
        else setModel(args[0]);
    }];
}

export function useMemoTuple<T>(tuple: T[]) {
    return useMemo(() => tuple, tuple);
}

export const useWindowSizeInRem = () => {
    let { width, height } = useWindowSize();
    width = pxInRem(width);
    height = pxInRem(height);
    return { width, height };
};
